// src/pages/QRManage.jsx
import { useState, useEffect } from 'react';
import { 
  Typography, 
  Button, 
  Box, 
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Snackbar,
  Alert,
  TablePagination,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormControl,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Fade,
  Zoom,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import LinkIcon from '@mui/icons-material/Link';
import WarningIcon from '@mui/icons-material/Warning';
import QrCodeIcon from '@mui/icons-material/QrCode';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import API_BASE_URL from '../apiConfig';

export default function QRManage() {
  const [qrs, setQrs] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [currentQR, setCurrentQR] = useState({ id: '', domain: '', target_url: '', location_note: '' });
  const [urlDomainOnly, setUrlDomainOnly] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Search filters
  const [filters, setFilters] = useState({
    id: '',
    domain: '',
    target_url: '',
    location_note: ''
  });

  useEffect(() => {
    fetchQRCodes();
  }, []);

  // Extract domain-only part when editing an existing QR code
  useEffect(() => {
    if (isEditing && currentQR.target_url) {
      const domainOnly = currentQR.target_url.replace(/^https?:\/\//, '');
      setUrlDomainOnly(domainOnly);
    } else {
      setUrlDomainOnly('');
    }
  }, [isEditing, currentQR.target_url]);

  const fetchQRCodes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/qr-list`, {
        credentials: 'include' // Include cookies with the request
      });
      if (response.ok) {
        const data = await response.json();
        setQrs(data);
      } else {
        showSnackbar('Failed to fetch QR codes', 'error');
      }
    } catch (error) {
      console.error('Error fetching QR codes:', error);
      showSnackbar('Error fetching QR codes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (qr = null) => {
    if (qr) {
      setCurrentQR(qr);
      setIsEditing(true);
      // Extract domain-only part when editing
      const domainOnly = qr.target_url.replace(/^https?:\/\//, '');
      setUrlDomainOnly(domainOnly);
    } else {
      setCurrentQR({ id: '', domain: '', target_url: '', location_note: '' });
      setUrlDomainOnly('');
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDeleteDialog = (qr) => {
    setCurrentQR(qr);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'urlDomainOnly') {
      // Only update the urlDomainOnly state without affecting target_url yet
      setUrlDomainOnly(value);
    } else {
      setCurrentQR({ ...currentQR, [name]: value });
    }
  };

  const handleSubmit = async () => {
    try {
      // Form validation
      if (!currentQR.id || !currentQR.domain || !urlDomainOnly) {
        showSnackbar('Please fill in all required fields', 'error');
        return;
      }
      
      // Ensure target_url always has https:// prefix
      const qrToSubmit = {
        ...currentQR,
        target_url: urlDomainOnly.startsWith('http') ? urlDomainOnly : `https://${urlDomainOnly}`
      };
      
      const url = isEditing ? `${API_BASE_URL}/api/qr-update/${qrToSubmit.id}` : `${API_BASE_URL}/api/qr-create`;
      const method = isEditing ? 'PUT' : 'POST';
      
      console.log('Submitting QR code:', qrToSubmit);
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Include cookies with the request
        body: JSON.stringify(qrToSubmit)
      });

      const responseData = await response.json();
      
      if (response.ok) {
        handleCloseDialog();
        fetchQRCodes();
        showSnackbar(isEditing ? 'QR code updated successfully' : 'QR code created successfully', 'success');
      } else {
        throw new Error(responseData.error || 'Failed to save QR code');
      }
    } catch (error) {
      console.error('Error saving QR code:', error);
      showSnackbar(error.message || 'Error saving QR code', 'error');
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/qr-delete/${currentQR.id}`, {
        method: 'DELETE',
        credentials: 'include' // Include cookies with the request
      });

      if (response.ok) {
        handleCloseDeleteDialog();
        fetchQRCodes();
        showSnackbar('QR code deleted successfully', 'success');
      } else {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to delete QR code', 'error');
      }
    } catch (error) {
      console.error('Error deleting QR code:', error);
      showSnackbar('Error deleting QR code', 'error');
    }
  };
  
  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      showSnackbar('Copied to clipboard!', 'success');
    }).catch(err => {
      showSnackbar('Failed to copy: ' + err, 'error');
    });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Handle filter changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setPage(0); // Reset to first page when filter changes
  };
  
  // Handle pagination changes
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Filter QR codes based on search criteria
  const filteredQRs = qrs.filter(qr => {
    return (
      qr.id.toLowerCase().includes(filters.id.toLowerCase()) &&
      qr.domain.toLowerCase().includes(filters.domain.toLowerCase()) &&
      qr.target_url.toLowerCase().includes(filters.target_url.toLowerCase()) &&
      (qr.location_note || '').toLowerCase().includes(filters.location_note.toLowerCase())
    );
  });
  
  // Apply pagination
  const paginatedQRs = filteredQRs.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Header with stats */}
      <Box mb={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <QrCodeIcon 
              fontSize="large" 
              sx={{ 
                mr: 2, 
                color: 'primary.main', 
                bgcolor: 'rgba(25, 118, 210, 0.1)', 
                p: 1, 
                borderRadius: '50%' 
              }} 
            />
            <Typography variant="h4" component="h2">
              Manage QR Codes
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 500,
              boxShadow: '0 4px 10px rgba(25, 118, 210, 0.25)',
              '&:hover': {
                boxShadow: '0 6px 12px rgba(25, 118, 210, 0.35)',
              }
            }}
          >
            Add New QR Code
          </Button>
        </Box>
        
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <QrCodeIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h3">
                    Total QR Codes
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" color="primary" fontWeight="bold">
                  {qrs.length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <LanguageIcon color="secondary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h3">
                    Unique Domains
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" color="secondary" fontWeight="bold">
                  {new Set(qrs.map(qr => qr.domain)).size}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" mb={1}>
                  <LocationOnIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h3">
                    QRs with Locations
                  </Typography>
                </Box>
                <Typography variant="h3" component="div" color="success.main" fontWeight="bold">
                  {qrs.filter(qr => qr.location_note && qr.location_note.trim()).length}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* Search filters */}
      <Paper elevation={2} sx={{ mb: 3, p: 3, borderRadius: 2 }}>
        <Typography variant="h6" component="div" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <SearchIcon sx={{ mr: 1 }} />
          Search Filters
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="QR ID"
              name="id"
              value={filters.id}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Domain"
              name="domain"
              value={filters.domain}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Target URL"
              name="target_url"
              value={filters.target_url}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Location"
              name="location_note"
              value={filters.location_note}
              onChange={handleFilterChange}
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOnIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Domain</TableCell>
              <TableCell>Target URL</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1" sx={{ mb: 1 }}>Loading QR codes...</Typography>
                    <Box sx={{ width: '100%', maxWidth: 600, height: 8, bgcolor: 'rgba(0,0,0,0.1)', borderRadius: 1, overflow: 'hidden' }}>
                      <Box 
                        sx={{ 
                          height: '100%', 
                          width: '30%', 
                          bgcolor: 'primary.main', 
                          borderRadius: 'inherit',
                          animation: 'loading 1.5s ease-in-out infinite',
                          '@keyframes loading': {
                            '0%': { transform: 'translateX(-100%)' },
                            '100%': { transform: 'translateX(350%)' }
                          }
                        }} 
                      />
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            ) : paginatedQRs.length > 0 ? (
              paginatedQRs.map((qr, index) => (
                <TableRow 
                  key={qr.id} 
                  hover
                  sx={{ 
                    animation: 'fadeIn 0.5s ease-in-out',
                    animationDelay: `${index * 0.05}s`,
                    '@keyframes fadeIn': {
                      '0%': { opacity: 0, transform: 'translateY(10px)' },
                      '100%': { opacity: 1, transform: 'translateY(0)' }
                    }
                  }}
                >
                  <TableCell>
                    <Chip 
                      variant="outlined" 
                      color="primary" 
                      size="small" 
                      label={qr.id} 
                      icon={<QrCodeIcon />} 
                    />
                  </TableCell>
                  <TableCell>{qr.domain}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Tooltip title="Copy URL to clipboard">
                        <IconButton 
                          size="small" 
                          onClick={() => handleCopyToClipboard(qr.target_url)}
                          sx={{ mr: 1 }}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={qr.target_url}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 250,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {qr.target_url}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {qr.location_note ? (
                      <Box display="flex" alignItems="center">
                        <LocationOnIcon color="success" fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="body2">{qr.location_note}</Typography>
                      </Box>
                    ) : (
                      <Chip 
                        label="No location" 
                        size="small" 
                        variant="outlined" 
                        color="default"
                        sx={{ fontSize: '0.75rem' }}
                        icon={<WarningIcon fontSize="small" />}
                      />
                    )}
                  </TableCell>
                  <TableCell>{new Date(qr.created_at).toLocaleString()}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="Edit QR code">
                        <IconButton 
                          onClick={() => handleOpenDialog(qr)} 
                          color="primary"
                          size="small"
                          sx={{ mx: 0.5 }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete QR code">
                        <IconButton 
                          onClick={() => handleOpenDeleteDialog(qr)} 
                          color="error"
                          size="small"
                          sx={{ mx: 0.5 }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {filteredQRs.length === 0 && qrs.length > 0 
                      ? 'No matching QR codes found' 
                      : 'No QR codes found'}
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<AddIcon />} 
                    onClick={() => handleOpenDialog()}
                  >
                    Create your first QR code
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Divider />
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={filteredQRs.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
        TransitionComponent={Zoom}
        TransitionProps={{
          timeout: 300
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center">
            {isEditing ? <EditIcon sx={{ mr: 1 }} /> : <AddIcon sx={{ mr: 1 }} />}
            {isEditing ? 'Edit QR Code' : 'Add New QR Code'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3} sx={{ mt: 1, minWidth: 400 }}>
            <TextField
              label="QR ID"
              name="id"
              value={currentQR.id}
              onChange={handleInputChange}
              fullWidth
              disabled={isEditing}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <QrCodeIcon color={isEditing ? "disabled" : "primary"} />
                  </InputAdornment>
                ),
              }}
              helperText={isEditing ? "QR ID cannot be changed" : "Enter a unique identifier for this QR code"}
            />
            <TextField
              label="Domain"
              name="domain"
              value={currentQR.domain}
              onChange={handleInputChange}
              fullWidth
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LanguageIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              helperText="Domain where the QR code will be hosted"
            />
            <FormControl fullWidth required>
              <InputLabel htmlFor="target-url-input">Target URL</InputLabel>
              <OutlinedInput
                id="target-url-input"
                name="urlDomainOnly"
                value={urlDomainOnly}
                onChange={handleInputChange}
                onFocus={(e) => e.target.select()}
                startAdornment={<InputAdornment position="start">https://</InputAdornment>}
                label="Target URL"
                placeholder="example.com"
                inputProps={{
                  autoComplete: "off"
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.75 }}>
                The URL users will be redirected to when scanning the QR code
              </Typography>
            </FormControl>
            <TextField
              label="Location Note"
              name="location_note"
              value={currentQR.location_note}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={2}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                    <LocationOnIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              helperText="Optional: Add a note about where this QR code is located"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!currentQR.id || !currentQR.domain || !urlDomainOnly}
            startIcon={isEditing ? <EditIcon /> : <AddIcon />}
          >
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={handleCloseDeleteDialog}
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          Delete QR Code
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the QR code with ID <strong>"{currentQR.id}"</strong>?
            <br /><br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button 
            onClick={handleCloseDeleteDialog}
            variant="outlined" 
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        TransitionComponent={Fade}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ 
            width: '100%',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
